import DOMPurify from "dompurify";

/**
 * リッチテキスト HTML から意図しない要素・属性を除去する。
 *
 * 保存済みの値は v-html や Quill の root.innerHTML に直接渡るため、DOM へ入る前に
 * ここを通す。エスケープ（文字をデータとして扱う変換）ではなく、HTML として
 * 解釈したうえで許可したものだけを残す allowlist 方式。
 */

// 既定エクスポートは全 import 元で共有されるため、そこへ addHook すると無関係な
// サニタイズにもフックが効いてしまう。専用インスタンスを作り、影響をこのモジュールに閉じる。
const purifier = DOMPurify(window);

// Quill が実際に出力する要素のみを許可する（ツールバーの全書式を適用して実測）。
// br は空行（<p><br></p>）で出る。ul は Quill 1 系で保存された既存データ用。
const ALLOWED_TAGS = ["p", "br", "span", "strong", "em", "u", "s", "a", "ol", "ul", "li"];

// contenteditable は Quill のエディタ内 UI（span.ql-ui）に付くもので、表示には不要。
// 許可しないことで、宿泊客の画面に編集可能な要素が生まれないようにする。
const ALLOWED_ATTR = ["class", "style", "href", "target", "rel", "data-list"];

// href に許可するスキーム。DOMPurify の既定から tel / sms / ftp 等を削り、
// 相対URLは従来どおり通す（後半2つの選択肢が相対URL用）。
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

// style 属性で許可する CSS プロパティ。Quill が inline style で出すのは文字色と
// 背景色だけで（font / size / align はクラス）、それ以外を通す必要がない。
// DOMPurify は既定で CSS プロパティを絞らないため、position:fixed による画面の
// 覆い被せがそのまま通ってしまう。ここで塞ぐ。
const ALLOWED_CSS_PROPERTIES = ["color", "background-color"];

// 色指定に必要な文字だけを許可し、url() や expression() を値に混ぜられないようにする。
const SAFE_CSS_VALUE = /^[a-z0-9#(),.%\s-]+$/i;

// Quill 由来のクラスのみ残す。Bootstrap がグローバルに読み込まれており
// .position-fixed 等のユーティリティクラスを使った覆い被せが可能なため、
// 任意のクラス名は通さない。
const QUILL_CLASS = /^ql-[\w-]+$/;

function filterStyle(value) {
  return value
    .split(";")
    .map(declaration => declaration.trim())
    .filter(Boolean)
    .reduce((kept, declaration) => {
      const separator = declaration.indexOf(":");
      if (separator === -1) return kept;

      const property = declaration.slice(0, separator).trim().toLowerCase();
      const propertyValue = declaration.slice(separator + 1).trim();
      if (ALLOWED_CSS_PROPERTIES.indexOf(property) === -1) return kept;
      if (!SAFE_CSS_VALUE.test(propertyValue)) return kept;

      kept.push(`${property}: ${propertyValue};`);
      return kept;
    }, [])
    .join(" ");
}

function filterClass(value) {
  return value
    .split(/\s+/)
    .filter(className => QUILL_CLASS.test(className))
    .join(" ");
}

// allowlist だけでは属性の「値」まで絞れないため、style と class はフックで精査する。
purifier.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName !== "style" && data.attrName !== "class") return;

  data.attrValue = data.attrName === "style"
    ? filterStyle(data.attrValue)
    : filterClass(data.attrValue);

  // 中身が全部落ちたら属性ごと消す（style="" が残らないように）。
  if (data.attrValue === "") data.keepAttr = false;
});

/**
 * HTML をサニタイズし、解析済みの DocumentFragment として返す。
 *
 * 文字列ではなく fragment を返すのは、呼び出し側（renderRichText）が続けて DOM を
 * 走査するため。ここで直列化して渡すと、同じ HTML を二度パースすることになる。
 *
 * @param {string} html
 * @returns {DocumentFragment}
 */
export function sanitizeToFragment(html) {
  return purifier.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    RETURN_DOM_FRAGMENT: true
  });
}
